import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { EntryType, JobContactEntry, UpdateJobContactEntryDto, WorkOrderVendor } from '../../types';
import { formatCurrency } from '../../utils/currency';
import { Upload, X, FileImage, FileText, File } from 'lucide-react';
import { JOB_ATTACHMENT_ACCEPT, JOB_ATTACHMENT_HELP_CONTENT, JOB_ATTACHMENT_HELP_TITLE, takeAllowedAttachments } from '../../utils/jobAttachments';
import HelpTooltip from '../ui/HelpTooltip';

interface EditTimelineEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: JobContactEntry | null;
  entryTypes: EntryType[];
  onSave: (entryId: number, data: UpdateJobContactEntryDto) => void;
  isLoading?: boolean;
  vendors?: WorkOrderVendor[]; // optional for vendor selection context
}

const EditTimelineEntryModal: React.FC<EditTimelineEntryModalProps> = ({
  isOpen,
  onClose,
  entry,
  entryTypes,
  onSave,
  isLoading,
  vendors,
}) => {
  const [message, setMessage] = useState('');
  const [editReason, setEditReason] = useState('');
  const [metadata, setMetadata] = useState<string | undefined>(undefined);
  // Structured fields for Include on Invoice
  const [qty, setQty] = useState<number | ''>('');
  const [rate, setRate] = useState<number | ''>('');
  const [isTaxable, setIsTaxable] = useState<boolean>(true);
  const [unit, setUnit] = useState<string>('');
  const [billableDesc, setBillableDesc] = useState<string>('');
  const [vendorId, setVendorId] = useState<number | undefined>(undefined);
  // File upload state
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!entry) return;
    setMessage(entry.message || '');
    setEditReason('');
    setMetadata(entry.metadata);
    setVendorId(entry.vendorId || undefined);

    // Initialize structured fields from existing metadata when financial entry
    try {
      if ((entry.entryTypeId === 5 || (entry.entryTypeName || '').toLowerCase().includes('include on invoice')) && entry.metadata) {
        const m: any = typeof entry.metadata === 'string' ? JSON.parse(entry.metadata) : entry.metadata;
        const qRaw = m.quantity ?? m.Quantity;
        const rRaw = m.rate ?? m.Rate;
        setQty(
          typeof qRaw === 'number' ? qRaw : (typeof qRaw === 'string' && qRaw.trim() !== '' && !isNaN(Number(qRaw)) ? Number(qRaw) : '')
        );
        setRate(
          typeof rRaw === 'number' ? rRaw : (typeof rRaw === 'string' && rRaw.trim() !== '' && !isNaN(Number(rRaw)) ? Number(rRaw) : '')
        );
        const tRaw = m.isTaxable ?? m.IsTaxable;
        setIsTaxable(typeof tRaw === 'boolean' ? tRaw : (typeof tRaw === 'string' ? tRaw.toLowerCase() === 'true' : true));
        setUnit(typeof m.unit === 'string' ? m.unit : (typeof m.Unit === 'string' ? m.Unit : ''));
        setBillableDesc(typeof m.description === 'string' ? m.description : (typeof m.Description === 'string' ? m.Description : ''));
      } else {
        setQty('');
        setRate('');
        setIsTaxable(true);
        setUnit('');
        setBillableDesc('');
      }
    } catch {
      setQty('');
      setRate('');
      setIsTaxable(true);
      setUnit('');
      setBillableDesc('');
    }
  }, [entry]);

  const entryType = useMemo(() => {
    if (!entry) return undefined;
    return entryTypes.find((t) => t.entryTypeId === entry.entryTypeId);
  }, [entry, entryTypes]);

  const isFinancialEntry = useMemo(() => {
    return entry?.entryTypeId === 5; // Include on Invoice
  }, [entry]);

  const isFileUploadEntry = useMemo(() => {
    return entry?.entryTypeId === 4; // File Upload
  }, [entry]);

  const parsedMetadata = useMemo(() => {
    if (!metadata) return undefined;
    try {
      return JSON.parse(metadata);
    } catch {
      return undefined;
    }
  }, [metadata]);

  // File upload handlers
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = takeAllowedAttachments(Array.from(event.target.files || []));
    if (files.length > 0) {
      setAttachedFiles(prev => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const files = takeAllowedAttachments(Array.from(event.dataTransfer.files));
    if (files.length > 0) {
      setAttachedFiles(prev => [...prev, ...files]);
    }
  };

  // Helper function to get file type icon
  const getFileTypeIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    if (['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'].includes(extension)) {
      return { Icon: FileImage, className: 'text-blue-500' };
    } else if (['.pdf', '.doc', '.docx', '.txt', '.rtf'].includes(extension)) {
      return { Icon: FileText, className: 'text-red-500' };
    } else {
      return { Icon: File, className: 'text-gray-500' };
    }
  };

  const handleSave = () => {
    if (!entry) return;
    if (!editReason.trim()) {
      // simple guard; page should use toast on mutation error
      return;
    }
    // For financial entries, synthesize metadata JSON from structured fields
    let finalMetadata: string | undefined = metadata;
    if (isFinancialEntry) {
      // Preserve PascalCase keys to match existing records and minimize noisy diffs
      const payload = {
        Quantity: qty === '' ? undefined : Number(qty),
        Rate: rate === '' ? undefined : Number(rate),
        IsTaxable: isTaxable,
        Description: billableDesc || undefined,
        Unit: unit || undefined,
      } as any;
      finalMetadata = JSON.stringify(payload);
    }
    const payload: UpdateJobContactEntryDto = {
      message: message || '',
      metadata: finalMetadata,
      vendorId: vendorId,
      editReason: editReason.trim(),
      attachments: attachedFiles.length > 0 ? attachedFiles : undefined,
    };
    onSave(entry.id, payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Timeline Entry"
      maxWidth="3xl"
      closeOnBackdropClick={false}
    >
      {entry && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-accent-color text-white !bg-accent-color">
                {entryType?.name || entry.entryTypeName}
              </span>
              <span className="text-xs text-gray-500">Entry ID #{entry.id}</span>
            </div>
          </div>

          {isFinancialEntry && (
            <div className="text-sm px-3 py-2 rounded-md bg-yellow-50 text-yellow-700 border border-yellow-200">
              This is a financial entry. Please provide a reason for the edit.
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Update the message"
              rows={4}
            />
          </div>

          {/* Vendor selection when relevant */}
          {vendors && vendors.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Vendor</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-sm"
                value={vendorId ?? ''}
                onChange={(e) => setVendorId(e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">None</option>
                {vendors.map((v) => (
                  <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
                ))}
              </select>
            </div>
          )}

          {/* Include on Invoice metadata editor (structured fields) */}
          {isFinancialEntry && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Quantity</label>
                  <Input
                    type="number"
                    value={qty}
                    onChange={(e) => setQty(e.target.value === '' ? '' : Number(e.target.value))}
                    min={0}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Rate</label>
                  <Input
                    type="number"
                    value={rate}
                    onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
                    min={0}
                    step="0.01"
                  />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={isTaxable}
                      onChange={(e) => setIsTaxable(e.target.checked)}
                    />
                    Taxable
                  </label>
                </div>
              </div>
          {/* Live subtotal */}
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {(() => {
              const q = typeof qty === 'number' ? qty : (qty === '' ? 0 : Number(qty));
              const r = typeof rate === 'number' ? rate : (rate === '' ? 0 : Number(rate));
              const amount = (isNaN(q) ? 0 : q) * (isNaN(r) ? 0 : r);
              return (
                <div className="inline-flex items-center gap-2 rounded-md bg-gray-50 dark:bg-gray-800 px-3 py-2 border border-gray-200 dark:border-gray-700">
                  <span className="font-medium">Subtotal:</span>
                  <span className="tabular-nums">{formatCurrency(amount)}</span>
                  <span className="text-xs text-gray-500">({q || 0} × {r || 0})</span>
                </div>
              );
            })()}
          </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Unit</label>
                  <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g. hours, items" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                  <Input value={billableDesc} onChange={(e) => setBillableDesc(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* File Upload section for File Upload entries */}
          {isFileUploadEntry && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Additional Files</div>
                <HelpTooltip
                  title={JOB_ATTACHMENT_HELP_TITLE}
                  content={JOB_ATTACHMENT_HELP_CONTENT}
                  position="bottom"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Add more files to this entry. Existing files will remain attached.
              </div>
              
              {/* Drag and Drop Area */}
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDrop={handleDrop}
              >
                <Upload className="h-8 w-8 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Drag and drop files here, or
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Photos, PDF, Word, Excel, and video up to 50 MB
                </div>
                <input
                  type="file"
                  multiple
                  accept={JOB_ATTACHMENT_ACCEPT}
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload-edit"
                />
                <label
                  htmlFor="file-upload-edit"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  Choose Files
                </label>
              </div>
              
              {/* File List */}
              {attachedFiles.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Selected Files</div>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {attachedFiles.map((file, index) => {
                      const { Icon, className } = getFileTypeIcon(file.name);
                      return (
                        <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <Icon className={`h-4 w-4 ${className} flex-shrink-0`} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="text-gray-400 hover:text-red-500 p-1 h-6 w-6"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Edit Reason</label>
            <Textarea
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="Why are you editing this entry?"
              rows={3}
            />
            {!editReason.trim() && (
              <div className="text-xs text-red-600">Edit reason is required.</div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={onClose} disabled={isLoading === true}>Cancel</Button>
            <Button onClick={handleSave} disabled={isLoading === true || !editReason.trim()}>{isLoading ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditTimelineEntryModal;


